<template>
  <v-app id="inspire">
    <v-app-bar app flat class="navbar-clean" height="70px" elevation="0">
      <!-- Container principal com padding -->
      <div class="navbar-content">
        <!-- Logo e Título -->
        <div class="brand-section">
          <a href="#" class="brand-link" @click.prevent="goHome">
            <div class="logo-container">
              <img
                width="36"
                height="36"
                src="@/assets/salvia_symbol.svg"
                alt="Salvia Logo"
                class="logo-image"
              />
            </div>
            <h1 class="brand-title">Salvia</h1>
          </a>
        </div>

        <v-spacer></v-spacer>

        <!-- Seletor de tema: fora do v-if de autenticação de propósito,
             pois é preferência do navegador e vale também deslogado. -->
        <ThemeSelect class="mr-1" />

        <!-- Menu do usuário autenticado -->
        <div v-if="isAuthenticated" class="user-section">
          <div class="user-controls">
            <ModeSelect />
            <NotificationBadge />
            <div class="controls-divider"></div>
            <UserMenu />
          </div>
        </div>

        <!-- Botões de autenticação para usuário não autenticado -->
        <div v-else class="auth-section">
          <router-link to="/login-page" class="auth-link">
            <v-btn class="login-btn" variant="text" size="default">
              <span class="login-text">Entrar</span>
            </v-btn>
          </router-link>

          <router-link
            :to="{ name: 'LoginPage', query: { step: '2' } }"
            class="auth-link"
          >
            <v-btn class="signup-btn" variant="outlined" size="default" rounded>
              <span class="signup-text">Cadastrar</span>
            </v-btn>
          </router-link>
        </div>
      </div>
    </v-app-bar>

    <v-main>
      <!-- A `key` força a tela a ser recriada quando o id do paciente na URL
           muda. Sem ela, o Vue Router reaproveita o mesmo componente quando só
           o parâmetro troca, e o `created()` não roda de novo: a tela continua
           mostrando o paciente ANTERIOR enquanto a URL já aponta para outro.
           Foi assim que um diálogo chegou a exibir um paciente e salvar noutro.

           `/patient-profile-dashboard/:id` é a única rota com parâmetro, e nas
           demais `params.id` é undefined — a chave não muda e nada é recriado,
           então o comportamento do resto do sistema fica igual. -->
      <router-view :key="$route.params.id" />
    </v-main>

    <!-- Snackbar Global -->
    <GlobalSnackbar />

    <!-- Dialog global de reconexão com Google (Camada 3) -->
    <GoogleReauthDialog />

    <!-- Dialog global de confirmação/feedback (this.$confirm) -->
    <GlobalConfirmDialog />

    <!--
      Os três atalhos flutuantes vivem dentro de um gatilho só: colapsados
      viravam uma coluna de botões cobrindo o canto da tela. O dock não sabe
      quem são — cada um mantém a própria regra de quando aparecer.
    -->
    <FloatingDock>
      <!-- Recado para Assistente (médico ↔ assistente) -->
      <AssistantNotesWidget v-if="isAuthenticated" />

      <!-- salvIA — só para médicos -->
      <ClinicalAssistantLauncher v-if="isAuthenticated" />

      <!--
        salvia-778 — ata de 13/08: iniciar uma consulta de QUALQUER tela, sem
        entrar no cadastro do paciente antes.

        Vive no dock, entao cumpre mais do que a ata pediu: ela falava em
        "agenda ou tela de pacientes", e aqui esta em todas. O proprio
        componente se esconde de quem nao e medico.
      -->
      <NovoAtendimentoLauncher v-if="isAuthenticated" />

      <!-- Tour guiado (o overlay em si é global; aqui só o botão de replay) -->
      <GuidedTour />
    </FloatingDock>

    <!-- salvia-779: "inicie a próxima consulta" / "a consulta vai começar" -->
    <ConsultaAlertaDialog v-if="isAuthenticated" />
  </v-app>
</template>

<script>
import NotificationBadge from "@/components/shared/NotificationBadge.vue";
import ThemeSelect from "@/components/shared/ThemeSelect.vue";
import UserMenu from "@/components/shared/UserMenu.vue";
import ModeSelect from "@/components/shared/ModeSelect.vue";
import GlobalSnackbar from "@/components/shared/GlobalSnackbar.vue";
import GoogleReauthDialog from "@/components/shared/GoogleReauthDialog.vue";
import GlobalConfirmDialog from "@/components/shared/GlobalConfirmDialog.vue";
import AssistantNotesWidget from "@/components/shared/AssistantNotesWidget.vue";
import FloatingDock from "@/components/shared/FloatingDock.vue";
import NovoAtendimentoLauncher from "@/features/patients/atendimento/NovoAtendimentoLauncher.vue";
import ClinicalAssistantLauncher from "@/features/assistant/ClinicalAssistantLauncher.vue";
import GuidedTour from "@/features/tour/GuidedTour.vue";
import ConsultaAlertaDialog from "@/features/patients/atendimento/ConsultaAlertaDialog.vue";

export default {
  components: {
    NotificationBadge,
    ThemeSelect,
    UserMenu,
    ModeSelect,
    GlobalSnackbar,
    GoogleReauthDialog,
    GlobalConfirmDialog,
    AssistantNotesWidget,
    FloatingDock,
    ClinicalAssistantLauncher,
    NovoAtendimentoLauncher,
    GuidedTour,
    ConsultaAlertaDialog,
  },
  computed: {
    isAuthenticated() {
      return this.$store.getters["auth/isAuthenticated"];
    },
    userType() {
      return this.$store.getters["auth/userType"];
    },
  },
  methods: {
    async goHome() {
      if (!this.isAuthenticated) return this.$router.push("/");

      await this.$store
        .dispatch("auth/fetchMe", { force: true })
        .catch(() => {});

      const t = this.$store.getters["auth/userType"];

      if (t === "assistant")
        return this.$router.push({
          path: "/calendar-page",
          query: { mode: "clinic" },
        });
      if (t === "doctor_solo" || t === "doctor_clinic")
        return this.$router.push({
          path: "/calendar-page",
          query: { mode: "doctor" },
        });
      return this.$router.push({ name: "PatientsPage" });
    },
  },
};
</script>

<style scoped>
/* ═══════════════════════════════════════
   Navbar
   ═══════════════════════════════════════ */
.navbar-clean {
  background: var(--ds-surface, var(--ds-surface)) !important; /* off-white */
  border-bottom: 1px solid #ffffff; /* branco */
  backdrop-filter: blur(12px) saturate(1.2);
  -webkit-backdrop-filter: blur(12px) saturate(1.2);
  z-index: 100;
  box-shadow: 0 2px 8px rgba(31, 77, 63, 0.04);
}

/* ═══════════════════════════════════════
   Container
   ═══════════════════════════════════════ */
.navbar-content {
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

/* ═══════════════════════════════════════
   Brand
   ═══════════════════════════════════════ */
.brand-section {
  display: flex;
  align-items: center;
}

.brand-link {
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  padding: 6px 8px 6px 6px;
  border-radius: 14px;
  transition: background 0.2s ease;
}

.brand-link:hover {
  background: rgba(0, 0, 0, 0.03);
}

.brand-link:active {
  background: rgba(0, 0, 0, 0.06);
}

.logo-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: linear-gradient(145deg, var(--ds-blue-50) 0%, var(--ds-blue-100) 100%);
  box-shadow: 0 1px 3px rgba(31, 77, 63, 0.08),
    0 0 0 1px rgba(31, 77, 63, 0.06);
  transition: box-shadow 0.2s ease, transform 0.2s ease;
  flex-shrink: 0;
}

.brand-link:hover .logo-container {
  box-shadow: 0 3px 10px rgba(31, 77, 63, 0.14),
    0 0 0 1px rgba(31, 77, 63, 0.08);
  transform: translateY(-0.5px);
}

.logo-image {
  display: block;
}

.brand-title {
  font-size: 22px;
  color: var(--ds-primary);
  margin: 0;
  letter-spacing: -0.4px;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI",
    sans-serif;
  user-select: none;
}

/* ═══════════════════════════════════════
   User Controls (autenticado)
   ═══════════════════════════════════════ */
.user-section {
  display: flex;
  align-items: center;
}

.user-controls {
  display: flex;
  align-items: center;
  gap: 6px;
}

.controls-divider {
  width: 1px;
  height: 24px;
  background: var(--ds-blue-100);
  margin: 0 6px;
  border-radius: 1px;
}

/* ═══════════════════════════════════════
   Auth Buttons (não autenticado)
   ═══════════════════════════════════════ */
.auth-section {
  display: flex;
  align-items: center;
  gap: 8px;
}

.auth-link {
  text-decoration: none;
}

.login-btn {
  font-weight: 500 !important;
  letter-spacing: 0.2px !important;
  height: 38px !important;
  padding: 0 18px !important;
  border-radius: 10px !important;
  transition: all 0.2s ease !important;
  box-shadow: none !important;
  background: transparent !important;
  border: none !important;
  min-width: auto !important;
}

.login-btn:hover {
  background: rgba(0, 0, 0, 0.04) !important;
}

.login-btn:active {
  background: rgba(0, 0, 0, 0.07) !important;
}

.login-text {
  color: #4a5568;
  font-size: 14px;
  font-weight: 500;
}

.login-btn:hover .login-text {
  color: #1a2332;
}

.signup-btn {
  border: 1.5px solid var(--ds-primary) !important;
  background: transparent !important;
  font-weight: 600 !important;
  letter-spacing: 0.2px !important;
  height: 38px !important;
  padding: 0 20px !important;
  border-radius: 10px !important;
  transition: all 0.2s ease !important;
  box-shadow: none !important;
  min-width: auto !important;
}

.signup-btn:hover {
  border-color: var(--ds-primary-dark) !important;
  background: rgba(31, 77, 63, 0.06) !important;
  box-shadow: 0 2px 8px rgba(31, 77, 63, 0.12) !important;
}

.signup-btn:active {
  background: rgba(31, 77, 63, 0.1) !important;
  box-shadow: none !important;
}

.signup-text {
  color: var(--ds-primary);
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.2px;
}

.signup-btn:hover .signup-text {
  color: var(--ds-primary-dark);
}

/* ═══════════════════════════════════════
   Responsividade
   ═══════════════════════════════════════ */
@media (max-width: 768px) {
  .navbar-content {
    padding: 0 16px;
  }

  .brand-title {
    font-size: 20px;
  }

  .user-controls {
    gap: 4px;
  }

  .controls-divider {
    margin: 0 4px;
  }

  .auth-section {
    gap: 6px;
  }

  .login-btn,
  .signup-btn {
    padding: 0 14px !important;
    height: 36px !important;
  }
}

@media (max-width: 480px) {
  .navbar-content {
    padding: 0 12px;
  }

  .brand-link {
    gap: 8px;
    padding: 4px 6px 4px 4px;
  }

  .brand-title {
    font-size: 18px;
  }

  .logo-container {
    width: 38px;
    height: 38px;
    border-radius: 10px;
  }

  .logo-image {
    width: 32px !important;
    height: 32px !important;
  }

  .controls-divider {
    display: none;
  }
}

/* ═══════════════════════════════════════
   Acessibilidade
   ═══════════════════════════════════════ */
.brand-link:focus-visible {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

.auth-link:focus-visible {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
  border-radius: 10px;
}

/* Remove outline no click, mantém no tab */
.brand-link:focus:not(:focus-visible),
.auth-link:focus:not(:focus-visible) {
  outline: none;
}
</style>
